/**
 * Floating combat text — Zone 2 DOM overlay over the battle canvas.
 *
 * Damage numbers ('-12 (34%)', DM Mono) float up off the defender and
 * fade; verdict pills (SUPER EFFECTIVE / RESISTED / CRITICAL / NO
 * EFFECT) pop as glass chips with a leading --matchup-* accent dot
 * (player-perspective — see hitFxConfig.verdictClass). Spawns are
 * anchored at canvas-relative coordinates captured at impact time
 * (NOT sprite-tracking, per the Zone 2 contract); the float travel is
 * pure CSS. Reduced motion: no travel — the keyframes swap to a
 * hold-and-fade via the media query in tokens.css.
 *
 * Glass restraint: the number is bare shadowed text (data over world,
 * not a panel); pills are flat translucent chips — no backdrop-filter.
 */
import type Phaser from 'phaser';
import { Z_DIALOGUE } from './zLayers';

export interface BattleFloatTextHandle {
  /** Damage number at canvas coords. tier widens the type at 'super'. */
  spawnDamage(x: number, y: number, text: string, tier: 'super' | 'neutral' | 'weak'): void;
  /** Verdict pill at canvas coords. cls maps to --matchup-* tokens. */
  spawnVerdict(x: number, y: number, label: string, cls: 'good' | 'bad' | 'none'): void;
  destroy(): void;
}

/** Hard ceiling on live float nodes — a runaway loop can't flood the
 *  DOM; oldest are evicted first. Normal flow peaks at ~3 per hit. */
const MAX_LIVE = 8;

/** Horizontal gutter between a sprite's edge and its float anchor —
 *  floats sit OUTBOARD of the pixel art, never over it (playtest
 *  round 2). Consumed by the damage handler's anchor math. */
export const FLOAT_GUTTER_PX = 22;
/** Minimum distance from the canvas edge after outboard placement. */
export const FLOAT_EDGE_PX = 28;

export function mountBattleFloatText(scene: Phaser.Scene): BattleFloatTextHandle {
  void scene; // parity with the other mount* signatures; DOM-only handle
  const parent = document.getElementById('game');
  const root = document.createElement('div');
  root.className = 'battle-float-layer';
  root.style.zIndex = String(Z_DIALOGUE - 1);
  parent?.appendChild(root);

  function spawn(el: HTMLSpanElement, x: number, y: number): void {
    el.style.left = `${Math.round(x)}px`;
    el.style.top = `${Math.round(y)}px`;
    while (root.childElementCount >= MAX_LIVE) root.firstElementChild?.remove();
    root.appendChild(el);
    // Removal on animation end, with a timeout backstop for the
    // animation-suppressed edge (display:none tab, etc.).
    let done = false;
    const remove = (): void => {
      if (done) return;
      done = true;
      el.remove();
    };
    el.addEventListener('animationend', remove);
    setTimeout(remove, 1600);
  }

  return {
    spawnDamage(x, y, text, tier): void {
      const el = document.createElement('span');
      el.className = 'battle-float battle-float--dmg';
      el.dataset['tier'] = tier;
      el.textContent = text;
      spawn(el, x, y);
    },
    spawnVerdict(x, y, label, cls): void {
      const el = document.createElement('span');
      el.className = `battle-float battle-float--pill battle-float--${cls}`;
      el.textContent = label;
      spawn(el, x, y);
    },
    destroy(): void {
      root.remove();
    },
  };
}
